import React from "react";
import { ADDONS_SERVICES, addonsFor } from "@/lib/api";
import { RIVIERA_SERVICES, RIVIERA_FREQUENCIES, RIVIERA_CITIES } from "@/lib/riviera";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Info } from "lucide-react";

const H = "text-2xl sm:text-3xl text-[#0F172A]";
const chip = (on) => `rounded-xl text-xs px-3 py-3 ring-1 transition-[background-color,box-shadow] ${on ? "bg-sky-50 ring-[#0EA5E9] text-sky-900" : "bg-white ring-slate-200 text-slate-700 hover:ring-slate-300"}`;
export const serviceKey = (v) => RIVIERA_SERVICES.find((s) => s.value === v)?.key;
export const supportsExtras = (v) => ADDONS_SERVICES.includes(v) || v === "Villa / Yacht-Season Turnover";

function Field({ label, req, children }) {
  return <div><Label>{label}{req && <span className="text-rose-500"> *</span>}</Label><div className="mt-1.5">{children}</div></div>;
}

export function StepService({ t, form, upd }) {
  return (
    <div className="space-y-7">
      <div>
        <h2 className={H}>{t("riviera.inquiry.service_title")}</h2>
        <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold mt-5 block">{t("riviera.inquiry.service_label")}</Label>
        <Select value={form.service_type} onValueChange={(v) => upd("service_type", v)}>
          <SelectTrigger className="mt-1.5" data-testid="fr-service"><SelectValue /></SelectTrigger>
          <SelectContent>
            {RIVIERA_SERVICES.map((s) => <SelectItem key={s.key} value={s.value} data-testid={`fr-service-option-${s.key}`}>{t(`riviera.services_list.${s.key}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        {serviceKey(form.service_type) && <p className="mt-2 text-xs text-slate-500">{t(`riviera.services_desc.${serviceKey(form.service_type)}`)}</p>}
      </div>
      <div>
        <h3 className="font-bold text-[#0F172A]">{t("riviera.inquiry.freq_title")}</h3>
        <p className="text-xs text-slate-500 mb-3">{t("riviera.inquiry.freq_help")}</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="fr-frequency">
          {RIVIERA_FREQUENCIES.map((f) => (
            <button type="button" key={f.value} onClick={() => upd("frequency", f.value)} className={chip(form.frequency === f.value)} data-testid={`fr-frequency-${f.value}`}>
              <span className="font-semibold">{t(f.labelKey)}</span>
            </button>
          ))}
        </div>
      </div>
      {supportsExtras(form.service_type) && (
        <div>
          <h3 className="font-bold text-[#0F172A]">{t("riviera.inquiry.extras_title")}</h3>
          <p className="text-xs text-slate-500 mb-3">{t("riviera.inquiry.extras_help")}</p>
          <div className="grid sm:grid-cols-2 gap-2" data-testid="fr-addons">
            {addonsFor(form.service_type).map((a) => {
              const on = form.addons.includes(a.key);
              return (
                <label key={a.key} className={`cursor-pointer flex justify-between items-center ${chip(on)} text-sm`} data-testid={`fr-addon-${a.key}`}>
                  <input type="checkbox" className="sr-only" checked={on}
                    onChange={(e) => upd("addons", e.target.checked ? [...form.addons, a.key] : form.addons.filter((k) => k !== a.key))} />
                  <span className="font-semibold">{t(`book_v2.extra_labels.${a.key}`, a.label)}</span>
                  {on && <CheckCircle2 className="w-4 h-4 text-[#0EA5E9]" />}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function StepProperty({ t, form, upd }) {
  const num = (k) => (e) => upd(k, e.target.value);
  return (
    <div className="space-y-5">
      <h2 className={H}>{t("book_v2.property.title")}</h2>
      <p className="text-xs text-slate-500">{t("riviera.inquiry.property_help")}</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t("book_v2.property.sqm")} req><Input type="number" min="0" value={form.sqm} onChange={num("sqm")} placeholder={t("book_v2.property.sqm_ph")} data-testid="fr-sqm" /></Field>
        <Field label={t("book_v2.property.floors")}>
          <Select value={String(form.floors)} onValueChange={(v) => upd("floors", parseInt(v))}>
            <SelectTrigger data-testid="fr-floors"><SelectValue /></SelectTrigger>
            <SelectContent>{[1, 2, 3, 4, 5, 6].map((n) => <SelectItem key={n} value={String(n)}>{n === 6 ? "6+" : n}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label={t("book_v2.property.bedrooms")}><Input type="number" min="0" value={form.bedrooms} onChange={num("bedrooms")} data-testid="fr-bedrooms" /></Field>
        <Field label={t("book_v2.property.bathrooms")}><Input type="number" min="0" value={form.bathrooms} onChange={num("bathrooms")} data-testid="fr-bathrooms" /></Field>
        <Field label={t("book_v2.property.client_type")}>
          <Select value={form.client_type} onValueChange={(v) => upd("client_type", v)}>
            <SelectTrigger data-testid="fr-client-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Residential / Private">{t("book_v2.property.client_type_residential")}</SelectItem>
              <SelectItem value="Commercial / B2B">{t("book_v2.property.client_type_commercial")}</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
    </div>
  );
}

export function StepSchedule({ t, form, upd }) {
  const v = (k) => ({ value: form[k], onChange: (e) => upd(k, e.target.value) });
  return (
    <div className="space-y-5">
      <h2 className={H}>{t("riviera.inquiry.schedule_title")}</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t("riviera.inquiry.city")} req>
          <Select value={form.city_slug} onValueChange={(s) => upd("city_slug", s)}>
            <SelectTrigger data-testid="fr-city"><SelectValue /></SelectTrigger>
            <SelectContent>{RIVIERA_CITIES.map((c) => <SelectItem key={c.slug} value={c.slug} data-testid={`fr-city-option-${c.slug}`}>{c.name}{c.inquiryOnly ? ` · ${t("riviera.inquiry_only")}` : ""}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label={t("riviera.inquiry.date")}><Input type="date" min={new Date().toISOString().slice(0, 10)} {...v("date")} data-testid="fr-date" /></Field>
        <Field label={t("riviera.inquiry.time")}><Input type="time" {...v("start_time")} data-testid="fr-time" /></Field>
        <Field label={t("book_v2.schedule.name")} req><Input {...v("customer_name")} data-testid="fr-name" /></Field>
        <Field label={t("book_v2.schedule.email")} req><Input type="email" {...v("customer_email")} data-testid="fr-email" /></Field>
        <Field label={t("book_v2.schedule.phone")} req><Input {...v("customer_phone")} placeholder={t("riviera.inquiry.phone_ph")} data-testid="fr-phone" /></Field>
        <div className="sm:col-span-2"><Field label={t("book_v2.schedule.address")} req><Input {...v("address")} placeholder={t("book_v2.schedule.address_ph")} data-testid="fr-address" /></Field></div>
        <Field label={t("book_v2.schedule.postal")}><Input {...v("postal_code")} placeholder={t("riviera.inquiry.postal_ph")} data-testid="fr-postal" /></Field>
      </div>
      <Field label={t("book_v2.schedule.access")}><Textarea {...v("access_instructions")} placeholder={t("book_v2.schedule.access_ph")} data-testid="fr-access" /></Field>
      <Field label={t("riviera.inquiry.requests")}><Textarea {...v("special_requests")} placeholder={t("riviera.inquiry.requests_ph")} data-testid="fr-requests" /></Field>
    </div>
  );
}

export function StepReview({ t, form, upd, rows }) {
  return (
    <div className="space-y-5">
      <h2 className={H}>{t("riviera.inquiry.review_title")}</h2>
      <dl className="rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-5 text-sm space-y-2" data-testid="fr-review-summary">
        {rows.map(([k, val]) => (
          <div key={k} className="flex justify-between gap-4"><dt className="text-slate-500">{k}</dt><dd className="font-semibold text-right text-[#0F172A]">{val || "—"}</dd></div>
        ))}
      </dl>
      <div className="flex gap-2 items-start rounded-2xl bg-sky-50 ring-1 ring-sky-200 p-4 text-sm text-sky-900" data-testid="fr-not-booking-note">
        <Info className="w-4 h-4 mt-0.5 shrink-0" /> {t("riviera.inquiry.not_booking")}
      </div>
      <label className="flex gap-3 items-start cursor-pointer">
        <Checkbox checked={form.consent} onCheckedChange={(c) => upd("consent", !!c)} className="mt-0.5" data-testid="fr-consent" />
        <span className="text-sm text-slate-700">{t("riviera.inquiry.consent")}</span>
      </label>
    </div>
  );
}
